import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { orderBy, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  WearableActivity,
  WearableConnection,
  WearableDailyMetrics,
  WearableProvider,
} from '@models';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

/**
 * Capa cliente de wearables. La conexión OAuth y la ingesta de datos las
 * realizan Cloud Functions (los tokens nunca llegan al cliente). Este servicio
 * lanza el flujo OAuth, consulta el estado de conexión y lee las métricas
 * ya ingeridas.
 */
@Injectable({ providedIn: 'root' })
export class WearableService {
  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
    private readonly functions: Functions,
  ) {}

  // ─── Conexiones ────────────────────────────────────────────────────────────

  connectionsByUser$(userId: string): Observable<WearableConnection[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<WearableConnection>(
          this.tenantContext.collections.WEARABLE_CONNECTIONS,
          where('userId', '==', userId),
        ),
      ),
    );
  }

  /**
   * Inicia el flujo OAuth con el proveedor. Devuelve la URL de autorización a la
   * que redirigir al deportista. El `redirectUri` es la pantalla de retorno de la
   * app/admin tras autorizar.
   */
  async startConnect(
    provider: WearableProvider,
    redirectUri: string,
  ): Promise<{ authUrl: string }> {
    const fn = httpsCallable<
      { provider: WearableProvider; redirectUri: string },
      { authUrl: string }
    >(this.functions, 'startWearableConnect');
    const res = await fn({ provider, redirectUri });
    return res.data;
  }

  /** Revoca la conexión y elimina las credenciales almacenadas. */
  async disconnect(provider: WearableProvider): Promise<void> {
    const fn = httpsCallable<
      { provider: WearableProvider },
      { success: boolean }
    >(this.functions, 'disconnectWearable');
    await fn({ provider });
  }

  // ─── Métricas diarias ──────────────────────────────────────────────────────

  dailyMetrics$(
    userId: string,
    days = 30,
  ): Observable<WearableDailyMetrics[]> {
    const since = new Date();
    since.setDate(since.getDate() - days);
    const sinceStr = since.toISOString().slice(0, 10);
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<WearableDailyMetrics>(
          this.tenantContext.collections.WEARABLE_DAILY_METRICS,
          where('userId', '==', userId),
          where('date', '>=', sinceStr),
          orderBy('date', 'asc'),
        ),
      ),
    );
  }

  // ─── Actividades ───────────────────────────────────────────────────────────

  activitiesByUser$(
    userId: string,
    days = 30,
  ): Observable<WearableActivity[]> {
    const since = new Date();
    since.setDate(since.getDate() - days);
    const sinceStr = since.toISOString();
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<WearableActivity>(
          this.tenantContext.collections.WEARABLE_ACTIVITIES,
          where('userId', '==', userId),
          where('startTime', '>=', sinceStr),
          orderBy('startTime', 'desc'),
        ),
      ),
    );
  }
}
